import { useEffect, useRef } from 'react'

import {
  Badge,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  Stack,
  Text,
  TextProps,
} from '@chakra-ui/react'
import dayjs from 'dayjs'
import {
  generatePath,
  Navigate,
  Link as ReactRouterLink,
  useParams,
} from 'react-router'
// eslint-disable-next-line no-restricted-imports -- 新しいアイコンに移行する
import { PaginationNext } from 'src/components/__legacy__/icon/monochrome'
import { FoldableAlert } from 'src/components/FoldableAlert/FoldableAlert'
import { LoadingDots } from 'src/components/LoadingDots/LoadingDots'
import { TextWithBar } from 'src/components/TextWithBar/TextWithBar'
import { VideoCallingBadge } from 'src/components/VideoCallingBadge/VIdeoCallingBadge'
import { Paths } from 'src/constants/paths'
import { httpStatus } from 'src/constants/statusCodes'
import {
  useAuthenticatedAccount,
  usePermission,
} from 'src/features/auth/context'
import { useMirohaToast } from 'src/lib/chakra-theme/components/toast/use-miroha-toast'
import { VideoCallRoom_ExplanationDetailScreenDocument } from 'src/lib/gql-client'
import { useGqlQuerySWRNoSuspense } from 'src/lib/gql-client/request'
import { PERMISSIONS } from 'src/lib/permission'
import { downloadObject } from 'src/utils/downloadObject'
import { formatDate } from 'src/utils/formatDate'
import { isNotNullish } from 'src/utils/isNotNullish'

import { UID } from '../../../../utils/brandedUid'
import {
  useDeliveryDocumentByDownload,
  useExplanation,
  useUpdateRevisionLastCheckedAt,
} from '../../api'
import { Explanation } from '../../types'
import { hasAlreadyPinSet } from '../../utils/hasAlreadyPinSet'
import { hasBeenAgreementCompleted } from '../../utils/hasBeenAgreementCompleted'
import { isAgreementCompleted } from '../../utils/isAgreementCompleted'
import { isPartner } from '../../utils/isPartner'
import { isPinSettingEnabledRevisionStatus } from '../../utils/isPinSettingEnabledRevisionStatus'
import { parseDocRevsToCompleteAgreement } from '../../utils/parseDocRevsToCompleteAgreement'
import { explanationRevisionStatusToText } from '../../utils/revisionStatusToText'
import { explanationTypeToText } from '../../utils/typeToText'
import { CompleteAgreementForDetail } from '../CompleteAgreementForDetail/CompleteAgreementForDetail'
import { DocSetAccordion } from '../DocSetAccordion/DocSetAccordion'
import { ExplanationHistoryDrawerButton } from '../ExplanationHistoryDrawerButton/ExplanationHistoryDrawerButton'
import { ExplanationMenu } from '../ExplanationMenu/ExplanationMenu'
import { ExplanationPatientInformationSection } from '../ExplanationPatientInformationSection/ExplanationPatientInformationSection'
import { ExplanationRequestPinSettingButton } from '../ExplanationRequestPinSettingButton/ExplanationRequestPinSettingButton'
import { ExplanationReservationCardV2 } from '../ExplanationReservationCardV2/ExplanationReservationCardV2'
import { ExplanationStartReAgreementButton } from '../ExplanationStartReAgreementButton/ExplanationStartReAgreementButton'
import { ExplanationVideoCallButton } from '../ExplanationVideoCallButton/ExplanationVideoCallButton'
import { ExplanationWithdrawHistoryButton } from '../ExplanationWithdrawHistoryButton/ExplanationWithdrawHistoryButton'
import { MembersTable } from '../MembersTable/MembersTable'
import { StartExplanationSession } from '../StartExplanationSession/StartExplanationSession'
import { SyncAllEnvelopes } from '../SyncAllEnvelopes/SyncAllEnvelopes'
import { UpdateDeliveryDate } from '../UpdateDeliveryDate/UpdateDeliveryDate'

type Param = {
  explanationUid: Explanation['uid']
}

const labelStyle: TextProps = {
  fontSize: 'sm',
  color: 'gray.600',
  fontWeight: 'bold',
}

export const ExplanationDetailScreen: React.FC = () => {
  const { hasPermission } = usePermission()

  const {
    account: {
      selectedTrial,
      selectedTrialHospitalUid,
      selectedTrialHospitalRole,
    },
  } = useAuthenticatedAccount()

  const { explanationUid = '' as UID<'Explanation'> } = useParams<Param>()

  const toast = useMirohaToast()

  const {
    data: explanation,
    error,
    mutate: mutateExplanation,
  } = useExplanation({
    explanationUid,
    revalidateOnMount: true,
    revalidateIfStale: false,
    revalidateOnFocus: false,
  })
  const { request: downloadDeliveredDocs } = useDeliveryDocumentByDownload({
    onSuccess: ({ downloadUrl }) => {
      downloadObject({ url: downloadUrl })
      mutateExplanation()
    },
    onError: error => {
      toast({
        status: 'error',
        title: error.message,
      })
    },
  })

  const hasShownPinSettingAlertRef = useRef(false)

  const { request: updateCheckedAt } = useUpdateRevisionLastCheckedAt({
    onSuccess: () => {
      mutateExplanation()
    },
    onError: error => {
      toast({
        status: 'error',
        title: error.message,
      })
    },
  })

  const { data: videoCallRoom } = useGqlQuerySWRNoSuspense(
    VideoCallRoom_ExplanationDetailScreenDocument,
    !!explanation
      ? { videoCallRoomUid: explanation.trialPatient.videoCallRoomUid }
      : null,
    {
      refreshInterval: 10000,
    },
  )

  useEffect(() => {
    if (!explanation || hasShownPinSettingAlertRef.current) return
    const pinStatus = explanation.latestRevision.pinSettingStatus
    const pinEnabled =
      isPinSettingEnabledRevisionStatus(explanation.latestRevision.statusV2) &&
      hasPermission(PERMISSIONS.Explanation_Edit)
    const hasPinSetNow = pinStatus ? hasAlreadyPinSet(pinStatus) : false
    const shouldShow = pinEnabled && !hasPinSetNow && pinStatus !== 'Completed'
    if (shouldShow) {
      hasShownPinSettingAlertRef.current = true
      toast({
        status: 'info',
        title: '署名実施時には患者の当人認証設定が必要です',
        description:
          '「当人認証設定」ボタンより患者の当人認証情報の事前設定が可能です。',
      })
    }
  }, [explanation, hasPermission, toast])

  if (error?.status === httpStatus.Forbidden) {
    return (
      <Navigate
        to={generatePath(Paths.TrialTop, {
          trialUid: selectedTrial.uid,
        })}
      />
    )
  }

  if (!explanation) return <LoadingDots />

  const {
    uid: latestRevisionUid,
    status,
    statusV2,
    type,
    deliveredAt,
    docSets,
    partnerTrialHospital,
    members,
    latestSessionUid,
    isLatestSessionFinished,
  } = explanation.latestRevision

  const canDownloadDeliveryDocs = (() => {
    if (!hasPermission(PERMISSIONS.Explanation_Edit)) return false
    return status === 'AgreementDone' || status === 'Withdrawn'
  })()

  const shouldShowReAgreementAlert = (() => {
    if (!hasPermission(PERMISSIONS.Explanation_Edit)) return false
    return explanation.isRequiredReAgreement
  })()

  const shouldShowDocUpdateAlert = (() => {
    if (!hasPermission(PERMISSIONS.Explanation_Edit)) return false
    if (status !== 'SessionNotStarted') return false
    return explanation.latestRevision.docSets.some(
      docSet => docSet.updatedAfterChecked,
    )
  })()

  const shouldShowVideoCallButton = (() => {
    if (!hasPermission(PERMISSIONS.Explanation_Edit)) {
      return false
    }
    if (selectedTrialHospitalRole === 'Partner' && type !== 'RemotePartner') {
      return false
    }
    // 最新の説明セッションが存在し、かつ終了していない場合はボタンを表示しない
    if (latestSessionUid && !isLatestSessionFinished) {
      return false
    }
    return isAgreementCompleted(statusV2)
  })()

  const pinSettingStatus = explanation.latestRevision.pinSettingStatus
  const hasPinSet = pinSettingStatus
    ? hasAlreadyPinSet(pinSettingStatus)
    : false

  const pinSettingEnabled =
    isPinSettingEnabledRevisionStatus(statusV2) &&
    hasPermission(PERMISSIONS.Explanation_Edit)

  const shouldShowInitialPinSettingButton = pinSettingEnabled && !hasPinSet

  const docRevisions = docSets
    .flatMap(docSet => docSet.explanationDocRevisions)
    .filter(isNotNullish)
  const { canComplete } = parseDocRevsToCompleteAgreement(docRevisions)

  const shouldShowNotAgreementDoneExistedAlert = (() => {
    return (
      (type === 'Remote' || type === 'RemotePartner') &&
      status === 'AgreementNotDone' &&
      !canComplete
    )
  })()

  const shouldShowCanAgreementDoneAlert = (() => {
    if (!hasPermission(PERMISSIONS.Explanation_Edit)) return false
    return (
      (type === 'Remote' || type === 'RemotePartner') &&
      status === 'AgreementNotDone' &&
      canComplete
    )
  })()

  const canSyncEnvelopes =
    type !== 'InPerson' &&
    status === 'AgreementNotDone' &&
    !canComplete &&
    !!latestSessionUid

  return (
    <Flex as="main" h="full" direction="column" py="6">
      <Box px="6">
        {shouldShowReAgreementAlert && (
          <Box mb="6">
            <FoldableAlert
              status="warning"
              title="説明の再同意が必要です"
              // partner施設の場合ボタンの表示等はないので省略
              description={
                isPartner(explanation, selectedTrialHospitalUid)
                  ? undefined
                  : '再同意実施ボタンから、説明の再同意を実施してください。'
              }
            />
          </Box>
        )}
        {shouldShowDocUpdateAlert && (
          <Box mb="6">
            <FoldableAlert
              status="warning"
              title="文書セット情報が更新されています"
              description="設定中の文書セットを確認の上、「更新文書を確認」ボタンを押下してください。"
            />
          </Box>
        )}
        {shouldShowNotAgreementDoneExistedAlert && (
          <Box mb="6">
            <FoldableAlert
              status="warning"
              title="署名が未完了の同意書があります"
              description="「同意書の最新状態を取得」ボタンを押して、署名状態を確認してください"
            />
          </Box>
        )}
        {shouldShowCanAgreementDoneAlert && (
          <Box mb="6">
            <FoldableAlert
              status="warning"
              title="説明の同意完了が可能です"
              description="「同意完了処理を実施」ボタンを押して、同意を完了できます。"
            />
          </Box>
        )}
        <Flex justify="space-between">
          <Breadcrumb
            display="flex"
            alignItems="center"
            separator={<PaginationNext size="M" />}
          >
            <BreadcrumbItem>
              <BreadcrumbLink
                as={ReactRouterLink}
                to={generatePath(Paths.Explanations, {
                  trialUid: selectedTrial.uid,
                })}
                color="blue.500"
                fontSize="lg"
                fontWeight="bold"
              >
                同意説明一覧
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem fontSize="lg" isCurrentPage>
              <BreadcrumbLink
                fontWeight="bold"
                cursor="text"
                _hover={{ textDecoration: 'none' }}
              >
                同意説明詳細
              </BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>
          <HStack spacing="4">
            {shouldShowInitialPinSettingButton && (
              <ExplanationRequestPinSettingButton
                latestRevisionUid={explanation.latestRevision.uid}
                patientUid={explanation.patient.uid}
                phoneNumber={explanation.patient.phoneNumber}
                mutateExplanation={() => {
                  mutateExplanation()
                }}
              />
            )}
            {shouldShowDocUpdateAlert && (
              <Button
                onClick={async () => {
                  updateCheckedAt({ explanationRevisionUid: latestRevisionUid })
                }}
              >
                更新文書を確認
              </Button>
            )}
            {shouldShowVideoCallButton && (
              <ExplanationVideoCallButton
                trialPatient={explanation.trialPatient}
                isPartnerMember={selectedTrialHospitalRole === 'Partner'}
                trialPatientUid={explanation.patient.trialPatientUid}
                explanationType={type}
                candidateId={explanation.patient.candidateId}
                mainHospitalName={explanation.trialHospital.name}
                partnerHospitalName={partnerTrialHospital?.name}
              />
            )}
            {!shouldShowDocUpdateAlert && (
              <ExplanationStartReAgreementButton explanation={explanation} />
            )}
            {!shouldShowDocUpdateAlert && (
              <StartExplanationSession
                explanation={explanation}
                mutateExplanation={mutateExplanation}
              />
            )}
            <ExplanationMenu
              explanation={explanation}
              mutateExplanation={mutateExplanation}
            />
          </HStack>
        </Flex>
      </Box>

      <Box
        as="section"
        aria-label="content"
        mt="6"
        flex="1"
        overflow="auto"
        px="6"
      >
        <ExplanationPatientInformationSection
          patient={explanation.patient}
          latestRevisionUid={explanation.latestRevision.uid}
          hasPinSet={hasPinSet}
          shouldShowRestPinSettingButton={pinSettingEnabled && hasPinSet}
          mutateExplanation={mutateExplanation}
        />
        <Box as="section" aria-labelledby="explanation_information_heading">
          <Box px="4" py="2" bg="blue.50">
            <TextWithBar as="h2" id="explanation_information_heading">
              同意取得状況
            </TextWithBar>
          </Box>
          <Box p="6">
            <Grid
              templateColumns="max-content 1fr"
              columnGap="64px"
              rowGap="5"
              alignItems="baseline"
            >
              <GridItem>
                <Text {...labelStyle}>ステータス</Text>
              </GridItem>
              <GridItem>
                <HStack alignItems="center" spacing="4" mb={2}>
                  {status === 'Withdrawn' ? (
                    <ExplanationWithdrawHistoryButton
                      explanationRevisionUid={latestRevisionUid}
                    />
                  ) : (
                    <Text>{explanationRevisionStatusToText(status)}</Text>
                  )}
                  {explanation.revisionUids.length >= 2 && (
                    <Badge>再同意</Badge>
                  )}
                  <ExplanationHistoryDrawerButton explanation={explanation} />
                </HStack>
                <HStack>
                  {hasPermission(PERMISSIONS.Explanation_Edit) && (
                    <CompleteAgreementForDetail explanation={explanation} />
                  )}
                  {canSyncEnvelopes && (
                    <SyncAllEnvelopes
                      buttonType="text"
                      sessionUid={latestSessionUid}
                      onSyncDone={() => mutateExplanation()}
                    />
                  )}
                </HStack>
              </GridItem>

              <GridItem>
                <Text {...labelStyle}>交付日</Text>
              </GridItem>
              <GridItem>
                <HStack spacing="4">
                  {!!deliveredAt && (
                    <Text>{formatDate(deliveredAt, 'YYYY/MM/DD (ddd)')}</Text>
                  )}
                  {(status === 'AgreementDone' || status === 'Withdrawn') && (
                    <UpdateDeliveryDate
                      explanation={explanation}
                      mutateExplanation={mutateExplanation}
                    />
                  )}
                </HStack>
              </GridItem>
              {canDownloadDeliveryDocs && (
                <>
                  <GridItem />
                  <GridItem>
                    <Box>
                      <Button
                        variant="outline"
                        onClick={async () => {
                          await downloadDeliveredDocs({
                            explanationRevisionUid: latestRevisionUid,
                            // 交付日が存在しない場合はlatestRevisionの交付日を更新する。
                            deliveryDate:
                              deliveredAt === undefined
                                ? dayjs(new Date()).format('YYYY-MM-DD')
                                : undefined,
                          })
                        }}
                      >
                        交付文書ダウンロード
                      </Button>
                    </Box>
                  </GridItem>
                </>
              )}
            </Grid>
          </Box>
        </Box>

        {
          // 対面同意は一度でも同意完了だけ面談情報を表示する
          (explanation.latestRevision.type !== 'InPerson' ||
            hasBeenAgreementCompleted(explanation.latestRevision.statusV2)) && (
            <Box as="section" aria-labelledby="explanation_information_heading">
              <HStack px="4" py="2" bg="blue.50">
                <TextWithBar as="h2" id="explanation_information_heading">
                  面談情報
                </TextWithBar>
                {!!videoCallRoom?.videoCallRoom.joinedMemberCount &&
                  videoCallRoom.videoCallRoom.joinedMemberCount > 0 && (
                    <VideoCallingBadge
                      joinMemberCount={
                        videoCallRoom.videoCallRoom.joinedMemberCount
                      }
                    />
                  )}
              </HStack>
              <Box w="full" maxW="840px" p={6}>
                <ExplanationReservationCardV2
                  explanationRevisionUid={latestRevisionUid}
                  revisionStatus={statusV2}
                  trialPatient={explanation.trialPatient}
                  explanationType={explanation.latestRevision.type}
                  isPartnerMemberNotAssigned={
                    type === 'RemotePartner' &&
                    members.filter(m => m.isPartner).length === 0
                  }
                  mainHospitalName={explanation.trialHospital.name}
                  partnerHospitalName={partnerTrialHospital?.name}
                  mutateExplanation={mutateExplanation}
                />
              </Box>
            </Box>
          )
        }

        <Box as="section" aria-labelledby="explanation_content_heading">
          <Box px="4" py="2" bg="blue.50">
            <TextWithBar as="h2" id="explanation_content_heading">
              説明内容
            </TextWithBar>
          </Box>
          <Stack p="6" spacing="8">
            <Box>
              <Text {...labelStyle}>対象文書</Text>
              <Box mt="2">
                <DocSetAccordion
                  type="fixed"
                  docSets={docSets}
                  shouldShowUpdateAlert={shouldShowDocUpdateAlert}
                />
              </Box>
            </Box>
            <Grid templateColumns="132px 1fr" rowGap="5" alignItems="center">
              <GridItem>
                <Text {...labelStyle}>説明方式</Text>
              </GridItem>
              <GridItem>
                <Text>{explanationTypeToText(type)}</Text>
              </GridItem>
              {type === 'RemotePartner' && (
                <>
                  <GridItem>
                    <Text {...labelStyle}>パートナー施設</Text>
                  </GridItem>
                  <GridItem>
                    <Text>{partnerTrialHospital?.name}</Text>
                  </GridItem>
                </>
              )}
              <GridItem>
                <Text {...labelStyle}>患者代諾者</Text>
              </GridItem>
              <GridItem>
                {/* 現状は固定で無し */}
                <Text>無し</Text>
              </GridItem>
            </Grid>
          </Stack>
        </Box>

        <Box as="section" aria-labelledby="explanation_members_heading">
          <Box px="4" py="2" bg="blue.50">
            <TextWithBar as="h2" id="explanation_members_heading">
              説明担当者
            </TextWithBar>
          </Box>
          <Box p="6">
            <MembersTable members={members} />
          </Box>
        </Box>
      </Box>
    </Flex>
  )
}
